import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconOthersFilled } from 'wg-fe-ui';
import { Flex, Text, Box, useTheme } from '@wegroup/design-system';

interface Props {
  editText?: string;
  removeText: string;
  onEdit?: () => void;
  onRemove: () => void;
}

const OptionButton: React.FC<React.PropsWithChildren<Props>> = ({
  editText,
  removeText,
  onEdit,
  onRemove,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  const { colors } = useTheme();
  const menuRef: React.RefObject<HTMLDivElement> = useRef(null);

  useEffect(() => {
    if (isMenuOpen)
      document.addEventListener('click', handleClickOutsideMenu, true);
    return () => {
      document.removeEventListener('click', handleClickOutsideMenu, true);
    };
  }, [isMenuOpen]);

  const handleClickOutsideMenu = (e: MouseEvent) => {
    const ref = menuRef?.current;
    const node = e.target as Node;
    if (ref && typeof node === 'object' && !ref.contains(node)) {
      if (isMenuOpen) setIsMenuOpen(false);
    }
    document.removeEventListener('click', handleClickOutsideMenu, true);
  };

  return (
    <Flex
      onClick={() => setIsMenuOpen((prev) => !prev)}
      data-test-id="ProFlow_OptionButton_Container"
      w="3.2rem"
      h="3.2rem"
      borderRadius="full"
      alignItems="center"
      justifyContent="center"
      position="absolute"
      top="2"
      right="2"
      bg="gray.100"
      cursor="pointer"
      transition="0.1s"
      _hover={{ bg: 'primary.500' }}
      sx={{ svg: { width: '90%' } }}
      {...otherProps}
    >
      <IconOthersFilled color="white" />
      {isMenuOpen && (
        <Box
          position="absolute"
          top="100%"
          mt="2"
          right="0"
          minW="22rem"
          bg="white"
          border={`1px solid ${colors.gray.strokeAndBg}`}
          borderRadius="md"
          boxShadow="level2"
          display="inline-block"
          zIndex="20"
          ref={menuRef}
        >
          {onEdit && (
            <Flex
              p="2"
              cursor="pointer"
              sx={{
                '+ div': { borderTop: `1px solid ${colors.gray.strokeAndBg}` },
                span: { color: colors.text.gray },
              }}
              onClick={onEdit}
              data-test-id="OptionButton_Edit"
            >
              <Text
                textStyle="normalBody"
                w="100%"
                p="2"
                borderRadius="md"
                _hover={{ bg: colors.gray.strokeAndBg }}
              >
                {editText}
              </Text>
            </Flex>
          )}
          <Flex
            p="2"
            cursor="pointer"
            onClick={onRemove}
            data-test-id="OptionButton_Remove"
          >
            <Text
              textStyle="normalBody"
              w="100%"
              p="2"
              borderRadius="md"
              _hover={{ bg: colors.gray.strokeAndBg }}
            >
              {t(removeText)}
            </Text>
          </Flex>
        </Box>
      )}
    </Flex>
  );
};

export default OptionButton;
